/**
 * Canonical QueryClient for ROOST-CRM (cluck.us).
 *
 * This is a MONEY platform. Defaults here are deliberately CONSERVATIVE:
 * we never want a stale balance, redemption status, or transaction row to
 * linger on screen and mislead a player about real funds.
 *
 * Defaults rationale
 * ------------------
 * - staleTime: 30s. Long enough to dedupe the burst of mount-time fetches
 *   that fire when a page composes several query hooks at once (the audit's
 *   "fetched twice per page" finding), short enough that money-adjacent data
 *   refreshes quickly. Per-query overrides drop this to 0 for the most
 *   sensitive reads (balances, redemption status) - see each hook.
 * - gcTime: 5min. Cache is dropped 5 minutes after the last observer
 *   unmounts, so navigating away and back is instant without holding stale
 *   money data indefinitely.
 * - retry: 1. apiClient ALREADY retries network/5xx errors up to 3 times
 *   (retryRequest in apiClient.js). We add exactly one react-query-level
 *   retry on top, and NEVER retry 4xx (a 401/403/404/409/422 is a real
 *   answer, not a transient blip - retrying wastes round-trips and can
 *   re-trigger rate limits).
 * - refetchOnWindowFocus: true. A player tabbing back to a money screen
 *   should see fresh data. Cheap given staleTime gating.
 * - refetchOnReconnect: true. Same reasoning after a dropped connection.
 *
 * Error surfacing
 * ---------------
 * apiClient currently swallows 403/5xx logging and rejects with a PLAIN
 * OBJECT (not an Error). That is owned by another agent and is NOT rewritten
 * here. To compensate WITHOUT touching apiClient, every query/mutation hook
 * routes through `normalizeQueryError` (see ./errors.js), which coerces the
 * rejected value back into a real Error with a usable `.message`, `.status`,
 * and `.code`. That is the boundary contract this whole data layer relies on.
 */

import { QueryClient } from "@tanstack/react-query";

/** 4xx responses are definitive answers, not transient failures. */
const isClientError = (error) => {
  const status = error?.status ?? error?.response?.status;
  return typeof status === "number" && status >= 400 && status < 500;
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (isClientError(error)) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      // Mutations move money/state. Never auto-retry - a duplicate POST to a
      // redemption/credit endpoint is exactly the double-spend class the
      // audit flags. Callers handle retry explicitly via user action.
      retry: false,
    },
  },
});

export default queryClient;
